import {
  Button,
  Card,
  Container,
  Row,
  Col,
  Table,
  Badge,
} from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchOrderById, editOrder } from "../../../Redux/ordersReduce";

function OrderDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useHistory();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const notify = (type, msg) => {
    if (type === 1)
      toast.success(
        <strong>
          <i className="fas fa-check-circle"></i>
          {msg}
        </strong>,
      );
    else
      toast.error(
        <strong>
          <i className="fas fa-exclamation-circle"></i>
          {msg}
        </strong>,
      );
  };

  const translateStatus = (status) => {
    const translations = {
      pending: "En attente",
      processing: "En cours de traitement",
      shipped: "Expédié",
      completed: "Terminé",
      cancelled: "Annulé",
    };
    return translations[status] || status;
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "processing":
        return "info";
      case "shipped":
        return "primary";
      case "completed":
        return "success";
      case "cancelled":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = ["pending", "processing", "shipped", "completed"];
    const currentIndex = statusFlow.indexOf(currentStatus);
    return currentIndex < statusFlow.length - 1
      ? statusFlow[currentIndex + 1]
      : currentStatus;
  };

  const updateOrderStatus = async (newStatus) => {
    try {
      setUpdating(true);
      await dispatch(editOrder({ id: id, status: newStatus })).unwrap();
      notify(
        1,
        `Statut de la commande mis à jour en ${translateStatus(newStatus)}`,
      );
      fetchOrder();
    } catch (error) {
      notify(2, error.message || "Échec de la mise à jour du statut");
    } finally {
      setUpdating(false);
    }
  };

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await dispatch(fetchOrderById(id)).unwrap();
      setOrder(response);
    } catch (error) {
      notify(2, "Échec du chargement des détails de la commande");
      navigate.push("/orders/list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <>
        <Container fluid>
          <Row>
            <Col md="12">
              <Card>
                <Card.Body>
                  <div className="text-center p-5">
                    <i className="fas fa-spinner fa-spin fa-3x"></i>
                    <p className="mt-3">Chargement...</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  if (!order) {
    return (
      <>
        <div className="wrapper">
          <Sidebar />
          <div className="main-panel">
            <AdminNavbar />
            <div className="content">
              <Container fluid>
                <Row>
                  <Col md="12">
                    <Card>
                      <Card.Body>
                        <div className="text-center p-5">
                          <p>Commande non trouvée</p>
                          <Button onClick={() => navigate.push("/orders/list")}>
                            Retour à la liste
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Container>
            </div>
            <Footer />
          </div>
        </div>
      </>
    );
  }

  const nextStatus = getNextStatus(order.status);
  const canUpdate =
    order.status !== "cancelled" && order.status !== "completed";

  return (
    <>
      <Container fluid>
        <ToastContainer />
        <br />
        <Row>
          <Col md="12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="title">Détails de la commande #{order.id}</h4>
              <Button
                variant="secondary"
                onClick={() => navigate.push("/orders/list")}
              >
                <i className="fas fa-arrow-left"></i> Retour
              </Button>
            </div>

            {/* Order Status Actions */}
            {canUpdate && nextStatus && nextStatus !== order.status && (
              <Card className="mb-4">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Statut actuel :</strong>{" "}
                      <Badge bg={getStatusBadgeColor(order.status)}>
                        {translateStatus(order.status)}
                      </Badge>
                    </div>
                    <Button
                      variant="success"
                      onClick={() => updateOrderStatus(nextStatus)}
                      disabled={updating}
                    >
                      {updating
                        ? "Traitement..."
                        : `Passer à ${translateStatus(nextStatus)}`}
                    </Button>
                    {order.status !== "cancelled" && (
                      <Button
                        variant="danger"
                        onClick={() => updateOrderStatus("cancelled")}
                        disabled={updating}
                      >
                        Annuler la commande
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Order Information */}
            <Row>
              <Col md="6">
                <Card className="mb-4">
                  <Card.Header>
                    <h5>Informations client</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table borderless>
                      <tbody>
                        <tr>
                          <td>
                            <strong>Nom complet :</strong>
                          </td>
                          <td>
                            {order.user?.firstName} {order.user?.lastName}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Email :</strong>
                          </td>
                          <td>{order.user?.email || "-"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Téléphone :</strong>
                          </td>
                          <td>{order.phoneNumber || "-"}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Adresse :</strong>
                          </td>
                          <td>{order.address || "-"}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>

              <Col md="6">
                <Card className="mb-4">
                  <Card.Header>
                    <h5>Informations commande</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table borderless>
                      <tbody>
                        <tr>
                          <td>
                            <strong>Date de commande :</strong>
                          </td>
                          <td>{formatDate(order.createdAt)}</td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Statut :</strong>
                          </td>
                          <td>
                            <Badge bg={getStatusBadgeColor(order.status)}>
                              {translateStatus(order.status)}
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Total :</strong>
                          </td>
                          <td>
                            <strong>{order.total?.toFixed(3)} TND</strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Order Items */}
            <Card>
              <Card.Header>
                <h5>Produits commandés</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive striped bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Produit</th>
                      <th>Prix unitaire</th>
                      <th>Quantité</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {console.log(order)}
                    {order.orderItems && order.orderItems.length > 0 ? (
                      order.orderItems.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            {item.product?.name || item.name || "-"}
                            {item.product?.reference && (
                              <small className="d-block text-muted">
                                Réf: {item.product.reference}
                              </small>
                            )}
                          </td>
                          <td>{item.price?.toFixed(3)} TND</td>
                          <td>{item.quantity}</td>
                          <td>
                            {(item.price * item.quantity)?.toFixed(3)} TND
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          Aucun produit dans cette commande
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="4" className="text-end">
                        <strong>Total :</strong>
                      </td>
                      <td>
                        <strong>{order.total?.toFixed(3)} TND</strong>
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default OrderDetails;
